/**
 * `useBrowsableEntities` — fetches the dashboard's `/api/catalog`
 * response once and returns the `browsable_entities` array, which is
 * the single source of truth for which entity slugs the data browser
 * surfaces (mirrors `BROWSABLE_ENTITIES` in
 * `apps/dashboard/src/repositories/entity-browser.ts`).
 *
 * Phase 20 follow-up — replaces a hardcoded list previously duplicated
 * in `pages/Data.tsx`, which drifted out of sync with the backend at
 * phase 20 (studio_secrets was missing from the SPA picker even though
 * the backend allow-list had been widened).
 *
 * Phase 67 — migrated to `useCachedResource` (key `catalog:browsable-entities`,
 * fetcher `api.catalog`). The module-level cache means switching to
 * `/data` and back renders the entity list instantly with no loading
 * flash; the value silently revalidates when stale.
 *
 * Returns `null` while loading (cold cache); `Error.message` propagates
 * as `error`.
 */

import { api } from "./api";
import { useCachedResource } from "./resource-cache";

export interface BrowsableEntitiesState {
  readonly entities: ReadonlyArray<string> | null;
  /** C1 — entity slug → group name, from the server. */
  readonly groups: Readonly<Record<string, string>>;
  /** C1 — entity slug → human label, from the server. */
  readonly labels: Readonly<Record<string, string>>;
  /** C1 — the rail's group order (business-first). */
  readonly groupOrder: ReadonlyArray<string>;
  readonly error: string | null;
}

/**
 * A rail entry that is NOT a database table.
 *
 * Discount codes live on Stripe; Layer knows them only through the metadata it
 * stamps. There is therefore no `coupons` table to browse — which is exactly
 * why they were absent from `/data`. They still belong in the rail beside the
 * money they discount, so this slug is injected client-side into the Money
 * group and the page renders a Stripe-backed view for it instead of a table.
 *
 * It is deliberately NOT added to `BROWSABLE_ENTITIES` server-side: that list
 * is exhaustively typed against real tables, and every consumer of it (cursor
 * pagination, PII column maps, cascade delete, the dev-reset classification
 * gate of invariant 15) assumes a table exists behind the name.
 */
export const NON_TABLE_VIEWS = {
  discount_codes: { group: "Money", label: "Discount codes" },
} as const;

export type NonTableView = keyof typeof NON_TABLE_VIEWS;

export function isNonTableView(slug: string): slug is NonTableView {
  return Object.prototype.hasOwnProperty.call(NON_TABLE_VIEWS, slug);
}

export function useBrowsableEntities(): BrowsableEntitiesState {
  const { data, error } = useCachedResource("catalog:browsable-entities", () =>
    api.catalog(),
  );
  const d = data as
    | {
        browsable_entities?: ReadonlyArray<string>;
        browsable_entity_groups?: Record<string, string>;
        browsable_entity_labels?: Record<string, string>;
        browsable_entity_group_order?: ReadonlyArray<string>;
      }
    | null
    | undefined;
  const serverEntities = d?.browsable_entities ?? null;
  const viewSlugs = Object.keys(NON_TABLE_VIEWS);
  return {
    // Non-table views ride alongside the server's tables so the rail, the
    // route guard and the group map all agree on one list.
    entities: serverEntities === null ? null : [...serverEntities, ...viewSlugs],
    // Empty defaults degrade to the old flat behaviour rather than crashing,
    // which matters for the window where a cached catalog response predates
    // this field.
    groups: {
      ...(d?.browsable_entity_groups ?? {}),
      ...Object.fromEntries(
        viewSlugs.map((s) => [s, NON_TABLE_VIEWS[s as NonTableView].group]),
      ),
    },
    labels: {
      ...(d?.browsable_entity_labels ?? {}),
      ...Object.fromEntries(
        viewSlugs.map((s) => [s, NON_TABLE_VIEWS[s as NonTableView].label]),
      ),
    },
    groupOrder: d?.browsable_entity_group_order ?? [],
    error: error?.message ?? null,
  };
}
